/**
 * "Learn the science": every concept, newest unread marked, read ones
 * dimmed. Shown in the Learning Lab under the front door, and on Learn
 * beside today's session (Coleman, 2026-10-08), the same component in both
 * so the two can't disagree. `bare` drops the Lab's divider above it, for
 * a host that puts it in its own card.
 */
import React from "react";
import { Check } from "lucide-react";
import { GOLD, LIGHT } from "../constants/theme";

/* A raised surface. White film on black, black film on white. */
const film = (C, a) => C !== LIGHT
  ? `rgba(255,255,255,${a})`
  : `rgba(0,0,0,${+(a * 0.6).toFixed(4)})`;

/* ── The concepts library ────────────────────────────────────────────────
 *
 * Concepts are also a lane, which is how the daily rotation serves one as
 * your session. This is the other way in, and it is deliberately not that:
 * no warmup, no finisher, no session logged, no rotation deciding which one
 * you get. You pick the idea you want and you read it.
 *
 * Both exist because they answer different questions. The lane answers "what
 * should I do today". This answers "I want to read the one about affordances",
 * which is the question a coach actually arrives with once there are more than
 * three of them, and the session flow has no good answer to it.
 */
export function ConceptLibrary({ C, concepts = [], onOpen, bare = false }) {
  if (!concepts.length) return null;
  const unread = concepts.filter(c => !c.read).length;
  // The newest concept, until it's opened, is marked as new: concepts are
  // highlighted here rather than as Learn's banner (Coleman, 2026-10-07).
  const newest = [...concepts].filter(c => c.added)
    .sort((a, b) => String(b.added).localeCompare(String(a.added)))[0];
  const newKey = newest && !newest.read ? newest.key : null;
  return (
    <div style={bare ? {} : { flexShrink: 0, marginTop: 26, paddingTop: 20,
      borderTop: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap",
        marginBottom: 3 }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 23, fontWeight: 400,
          letterSpacing: 1.6, textTransform: "uppercase", color: C.text, lineHeight: 1 }}>
          Learn the science
        </div>
        <span style={{ fontSize: 11.5, color: C.textDim }}>
          {unread > 0
            ? `${unread} you haven't read`
            : `All ${concepts.length} read`}
        </span>
      </div>
      <div style={{ fontSize: 12.5, color: C.textDim, lineHeight: 1.5, marginBottom: 12 }}>
        The science and philosophy underneath the work. Read any of them, any
        time. This is not a session, so nothing here starts one.
      </div>
      <div style={{ display: "grid", gap: 9,
        gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
        {concepts.map(c => (
          <button key={c.key} type="button" className="btn bam-lane-card"
            onClick={() => onOpen?.(c)}
            style={{ textAlign: "left", padding: "13px 14px", borderRadius: 11,
              fontFamily: "inherit", cursor: "pointer", background: film(C, 0.022),
              border: `1px solid ${c.key === newKey ? `${GOLD}99` : C.border}`, opacity: c.read ? 0.72 : 1 }}>
            {c.key === newKey && (
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.3, color: GOLD, marginBottom: 6 }}>NEW</div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: C.text, flex: 1,
                minWidth: 0 }}>{c.title}</span>
              {c.read
                ? <Check size={13} color={C.textDim} style={{ flexShrink: 0 }}/>
                : <span style={{ flexShrink: 0, width: 7, height: 7, borderRadius: "50%",
                    background: GOLD }}/>}
            </div>
            <div style={{ fontSize: 12, color: C.textDim, lineHeight: 1.45,
              marginBottom: 6 }}>{c.blurb}</div>
            <div style={{ fontSize: 11, color: C.textDim, fontWeight: 700 }}>
              {c.minutes} min read
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default ConceptLibrary;
